import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { imgUrl } from "../../lib/format";
import {
  HERO_SHOWCASE_FRAMES,
  HERO_SHOWCASE_FAMILY,
  HERO_ROTATION_MS,
  allShowcaseSlugs,
} from "../../lib/hero-showcase-frames";

/**
 * Homepage hero — brand-dominant composition (Feb 2026 redesign).
 *
 * Vial showcase (Feb-2026 update):
 *   * Three real catalog vials rotate as a COORDINATED trio every
 *     HERO_ROTATION_MS ms (5 s by default).
 *   * A single frame index drives all three slots — the trio ALWAYS
 *     changes together, never a single-item shift.
 *   * Every visible frame carries three distinct research-family keys
 *     (no two CJC-family, no two GLP-family, etc.) — enforced by both
 *     `hero-showcase-frames.js` and its test file.
 *   * Rotation pauses while the mouse is over the showcase OR while
 *     keyboard focus is inside it, and remains paused for users with
 *     `prefers-reduced-motion: reduce` (they see the first frame with
 *     no autoplay).
 *   * Never causes a layout shift — the container is a flex row and
 *     each image transitions opacity via the shared `animate-fade-in`
 *     keyframe when its slug changes.
 */

// Canonical rectangular brand-mark asset — used by hero + footer.  The
// header uses a separate circular asset in `components/brand/HeaderLogo.jsx`.
const BRAND_LOGO_SRC =
  "https://customer-assets-jt897jd0.emergentagent.net/job_tender-goldberg-9/artifacts/1kwkf9ch_Generated%20image%201%20%285%29.png";

/** Detect `prefers-reduced-motion: reduce`.  Server-safe. */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (_e) {
      return false;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    let mq;
    try { mq = window.matchMedia("(prefers-reduced-motion: reduce)"); } catch (_e) { return; }
    const handler = (e) => setReduced(!!e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);
  return reduced;
}

export function HomeHero() {
  const [products, setProducts] = useState({}); // slug → product
  const [frameIdx, setFrameIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = usePrefersReducedMotion();
  const containerRef = useRef(null);

  const slugs = useMemo(() => allShowcaseSlugs(), []);

  // Fetch every showcase product once.  Uses the public /products endpoint
  // (paginated, but we know all our slugs are in the 106-SKU active
  // catalog) so a single call returns everything we need.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/products", { params: { limit: 200 } })
      .then((r) => {
        if (cancelled) return;
        const all = r.data.items || [];
        const bySlug = {};
        for (const p of all) {
          if (!slugs.includes(p.slug)) continue;
          if (!p.primary_image_url && !p.thumbnail_image_url) continue;
          bySlug[p.slug] = p;
        }
        setProducts(bySlug);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slugs]);

  // Coordinated rotation — ONE interval, pauses on hover/focus, respects
  // reduced-motion.  Guarded so we never spin when the fetch hasn't
  // returned yet or the tab is unmounted.
  useEffect(() => {
    if (reduced || paused) return undefined;
    if (Object.keys(products).length < 3) return undefined;
    const id = setInterval(() => {
      setFrameIdx((i) => (i + 1) % HERO_SHOWCASE_FRAMES.length);
    }, HERO_ROTATION_MS);
    return () => clearInterval(id);
  }, [reduced, paused, products]);

  // Pause helpers — memoized so re-renders don't churn listeners.
  const handlePause = useCallback(() => setPaused(true), []);
  const handleResume = useCallback(() => setPaused(false), []);

  // Compose the visible trio.  If a slug is still loading we skip it and
  // fall back to whatever data we have — no null-image slot ever renders.
  const currentSlugs = HERO_SHOWCASE_FRAMES[frameIdx];
  const currentTrio = currentSlugs
    .map((s) => products[s])
    .filter(Boolean);

  return (
    <section
      className="relative overflow-hidden bg-brand-navy text-white"
      data-testid="home-hero"
    >
      {/* Rich navy-to-electric-blue background — no clinical grid. */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b2a52] via-[#0A2342] to-[#081b36]" />
        <div
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              "radial-gradient(65% 55% at 50% 0%, rgba(47,128,237,0.42) 0%, transparent 60%), radial-gradient(50% 45% at 12% 90%, rgba(47,128,237,0.28) 0%, transparent 55%), radial-gradient(40% 35% at 88% 78%, rgba(47,128,237,0.22) 0%, transparent 55%)",
          }}
        />
        {/* Soft warm halo to move the palette out of clinical territory. */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(60% 45% at 50% 40%, rgba(140,190,255,0.18) 0%, transparent 70%)",
          }}
        />
      </div>

      <div className="relative mps-container pt-16 lg:pt-20 pb-20 lg:pb-24 text-center">
        {/* BRAND ANCHOR — dominant. */}
        <div className="relative inline-block mx-auto mb-10 lg:mb-12">
          <div
            className="absolute -inset-16 rounded-full opacity-70 blur-3xl bg-brand-accent/25 pointer-events-none"
            aria-hidden
          />
          <img
            src={BRAND_LOGO_SRC}
            alt="Medical Peptide Store"
            data-testid="hero-brand-logo"
            loading="eager"
            decoding="async"
            className="relative block w-[240px] sm:w-[300px] md:w-[380px] lg:w-[420px] h-auto mx-auto select-none drop-shadow-[0_18px_60px_rgba(47,128,237,0.45)]"
            draggable="false"
          />
        </div>

        <h1
          data-testid="hero-h1"
          className="font-display text-[34px] sm:text-[42px] md:text-5xl lg:text-[56px] font-bold tracking-tight leading-[1.08] !text-white max-w-3xl mx-auto"
        >
          Premium research peptides,{" "}
          <span className="!text-brand-accent">made simple.</span>
        </h1>

        <p
          data-testid="hero-body"
          className="mt-6 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl mx-auto font-light"
        >
          Shop{" "}
          <strong
            className="font-semibold text-white"
            data-testid="hero-catalog-count"
          >
            105 compounds
          </strong>{" "}
          with clear batch information and secure checkout.
        </p>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/shop?featured=1"
            data-testid="hero-primary-cta"
            className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-brand-accent hover:bg-[#1E6BD8] text-white font-semibold text-base shadow-[0_12px_36px_rgba(47,128,237,0.42)] transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-[1px]"
          >
            Shop Best Sellers <ArrowRight size={16} weight="bold" />
          </Link>
          <Link
            to="/shop"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-white/10 hover:bg-white/15 border border-white/25 text-white font-semibold text-base transition-colors duration-200"
          >
            Browse All Peptides
          </Link>
        </div>

        {/* Compact supported-fact chip row.  Sits right under the CTAs so
            first-time shoppers see the four most conversion-relevant
            operational facts before scrolling to the full trust strip.
            Every string here is defensible from checkout + policy.
            Feb-2026: "Made in the USA" is the FIRST chip after owner
            confirmed all peptides are manufactured in the United States
            — do NOT extend the claim to raw materials or labs. */}
        <ul
          data-testid="hero-value-chips"
          className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[12px] text-white/85"
        >
          <li
            data-testid="hero-chip-made-in-usa"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 border border-white/25 font-semibold"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" aria-hidden />
            Made in the USA
          </li>
          <li className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" aria-hidden />
            Live carrier rates at checkout
          </li>
          <li className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" aria-hidden />
            Signature required on delivery
          </li>
          <li className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" aria-hidden />
            Batch documents on request
          </li>
        </ul>

        {/* Coordinated product showcase — three real vials, rotates
            every HERO_ROTATION_MS ms while unpaused. */}
        {currentTrio.length === 3 && (
          <div
            ref={containerRef}
            data-testid="hero-product-showcase"
            data-frame-index={frameIdx}
            data-paused={paused ? "true" : "false"}
            data-reduced-motion={reduced ? "true" : "false"}
            className="mt-16 lg:mt-20 flex flex-wrap items-end justify-center gap-6 md:gap-10 lg:gap-14 min-h-[16rem] md:min-h-[18rem] lg:min-h-[19rem]"
            onMouseEnter={handlePause}
            onMouseLeave={handleResume}
            onFocus={handlePause}
            onBlur={(e) => {
              // Only resume when focus truly leaves the container.
              if (!containerRef.current) return;
              if (!containerRef.current.contains(e.relatedTarget)) {
                handleResume();
              }
            }}
          >
            {currentTrio.map((p, i) => {
              const label = p.public_display_name || p.product_name;
              const alt = p.image_alt_text || `${label} vial`;
              return (
                <Link
                  key={`slot-${i}`}
                  to={`/product/${p.slug}`}
                  data-testid={`hero-vial-${i}`}
                  data-slug={p.slug}
                  data-family={HERO_SHOWCASE_FAMILY[p.slug] || "unknown"}
                  className="group relative block transition-transform duration-300 hover:-translate-y-1"
                  aria-label={label}
                >
                  {/* keyed on slug → React swaps the img on frame change,
                      which re-triggers the shared 400 ms fade-in keyframe.
                      Layout stays put because widths are natural (`w-auto`)
                      but the parent has a min-height. */}
                  <img
                    key={p.slug}
                    src={imgUrl(p.primary_image_url || p.thumbnail_image_url)}
                    alt={alt}
                    loading="lazy"
                    className={`relative block h-40 sm:h-48 md:h-56 lg:h-64 w-auto object-contain drop-shadow-[0_25px_45px_rgba(0,0,0,0.5)] ${
                      i === 1 ? "md:h-64 lg:h-72" : ""
                    } ${reduced ? "" : "animate-fade-in"}`}
                  />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
